import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { ArrowLeft, Inbox, Check, X, Loader2, Trash2, PencilLine } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useAuth } from '@/lib/auth';

/**
 * The super_admin's queue: everything other users have asked to change.
 *
 * Approving an UPDATE applies it here. Approving a DELETE does not delete - it
 * records the decision and sends you to the record, where the existing drawer
 * counts dependencies, demands a reason and gates force. Those guards are the
 * reason deleting a driver does not orphan its trips, and a queue that bypassed
 * them would be worse than no queue at all.
 */

interface CR {
  id: string;
  tableName: 'drivers' | 'vehicles';
  recordId: string;
  reason: string;
  proposedChanges: { action: 'update' | 'delete'; fields: Record<string, unknown> | null } | null;
  status: string;
  requestedAt: string;
  requestedByName: string | null;
  label: string | null;
}

const STATUSES = ['pending', 'approved', 'rejected', 'all'] as const;

export function ChangeRequestsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('pending');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const isSuper = user?.role === 'super_admin';

  const { data, isLoading } = useQuery({
    queryKey: ['change-requests', status],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: CR[] }>('/api/v1/change-requests', { params: { status } });
      return data.items;
    },
    enabled: isSuper,
    staleTime: 15 * 1000,
  });

  const decide = async (cr: CR, verb: 'approve' | 'reject') => {
    setBusy(cr.id); setError(null); setNote(null);
    try {
      const { data } = await apiClient.post<{ applied?: boolean; message?: string }>(
        `/api/v1/change-requests/${cr.id}/${verb}`,
        { notes: notes[cr.id]?.trim() || undefined },
      );
      // Say plainly whether anything actually changed. "Approved" on a delete
      // means the decision is recorded, not that the record is gone.
      setNote(verb === 'reject'
        ? 'Rejected.'
        : data.applied
          ? 'Approved and applied.'
          : (data.message ?? 'Approved. Finish it on the record itself.'));
      await qc.invalidateQueries({ queryKey: ['change-requests'] });
      void qc.invalidateQueries({ queryKey: ['drivers'] });
      void qc.invalidateQueries({ queryKey: ['vehicles'] });
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  if (!isSuper) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <p className="text-sm text-muted-foreground">
          Only a super admin can review change requests.
        </p>
      </div>
    );
  }

  const items = data ?? [];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2"><LanguageSwitcher /><ThemeToggle /></div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <Link to="/dashboard/settings" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className="h-3 w-3" /> Back
        </Link>
        <div className="flex items-center gap-3">
          <Inbox className="h-7 w-7 text-gold" />
          <h1 className="font-display text-3xl text-foreground">Change requests</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1 mb-6">
          Edits and deletions other users have asked for. Approving an edit applies it;
          approving a deletion records the decision and sends you to the record to finish it.
        </p>

        <div className="flex gap-2 mb-4">
          {STATUSES.map((sfilter) => (
            <button
              key={sfilter}
              type="button"
              onClick={() => setStatus(sfilter)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium capitalize transition-colors ${
                status === sfilter ? 'border-gold bg-gold/10 text-foreground'
                                   : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {sfilter}
            </button>
          ))}
        </div>

        {note && <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">{note}</div>}
        {error && <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm">{error}</div>}

        {isLoading && <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>}

        {!isLoading && items.length === 0 && (
          <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground text-sm">
            Nothing {status === 'all' ? 'here' : status}.
          </div>
        )}

        <div className="space-y-3">
          {items.map((cr) => {
            const action = cr.proposedChanges?.action ?? 'update';
            const fields = cr.proposedChanges?.fields ?? null;
            return (
              <div key={cr.id} className="bg-card border border-border rounded-lg p-4">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium ${
                    action === 'delete'
                      ? 'bg-rose-500/15 text-rose-600 dark:text-rose-400'
                      : 'bg-sky-500/15 text-sky-600 dark:text-sky-400'
                  }`}>
                    {action === 'delete' ? <Trash2 className="h-3 w-3" /> : <PencilLine className="h-3 w-3" />}
                    {action}
                  </span>
                  <span className="text-foreground font-medium">{cr.label ?? cr.recordId}</span>
                  <span className="text-xs text-muted-foreground">
                    {cr.tableName === 'drivers' ? 'driver' : 'vehicle'}
                    {' · asked by '}{cr.requestedByName ?? 'someone'}
                    {' · '}{new Date(cr.requestedAt).toLocaleString('en-GB')}
                  </span>
                  {cr.status !== 'pending' && (
                    <span className="ms-auto text-xs text-muted-foreground capitalize">{cr.status}</span>
                  )}
                </div>

                <p className="text-sm text-foreground mb-2">“{cr.reason}”</p>

                {fields && Object.keys(fields).length > 0 && (
                  <div className="text-xs text-muted-foreground mb-3 space-y-0.5">
                    {Object.entries(fields).map(([k, v]) => (
                      <div key={k}>
                        <span className="font-mono">{k}</span> → <b className="text-foreground">{String(v)}</b>
                      </div>
                    ))}
                  </div>
                )}

                {cr.status === 'pending' && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      value={notes[cr.id] ?? ''}
                      onChange={(e) => setNotes((p) => ({ ...p, [cr.id]: e.target.value }))}
                      placeholder="Note back to them (optional)"
                      className="h-9 max-w-xs text-sm"
                    />
                    <Button variant="gold" disabled={busy === cr.id} onClick={() => void decide(cr, 'approve')}>
                      {busy === cr.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      Approve
                    </Button>
                    <Button variant="outline" disabled={busy === cr.id} onClick={() => void decide(cr, 'reject')}>
                      <X className="h-4 w-4" /> Reject
                    </Button>
                    <Link
                      to={cr.tableName === 'drivers' ? '/dashboard/drivers' : '/dashboard/vehicles'}
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                    >
                      open the record
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

export default ChangeRequestsPage;
